// Movatic WMS — Frontend | CesarFilho 932048016
// ADR-0042 errata E7 (FASE BASE): gerador de cenarios em lote (DV01).
// So' o tipo Venda esta' ativo; tipos e adicionais "em breve" acendem por fase.
import { useState } from 'react'
import {
  FlaskConical,
  ShoppingCart,
  Package,
  Box,
  ClipboardList,
  Play,
  CircleCheck,
  AlertTriangle,
  XCircle,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/store/authStore'

type OrderReport = {
  order_id?: number
  customer_code?: string
  customer_id?: number
  status?: string
  reservations?: number
  fully_allocated?: boolean
  error?: string
  lines?: { product_code: string; qty_ordered: string }[]
}
type BatchReport = {
  pool_size: number
  condition?: string | null
  customers_used: number
  orders_total: number
  orders_allocated: number
  orders_partial_or_short: number
  orders: OrderReport[]
}

const FAMILIAS: { titulo: string; itens: string[] }[] = [
  {
    titulo: 'Disponibilidade / alocação',
    itens: [
      'Produto sem stock',
      'Alocação parcial forçada',
      'Produto inexistente',
    ],
  },
  {
    titulo: 'Lote · FIFO / FEFO',
    itens: ['Lote obrigatório', 'Seleção FEFO (validade)', 'Shelf-life mínimo'],
  },
  {
    titulo: 'Identidade / rastreabilidade',
    itens: [
      'Pallet identificado (SSCC)',
      'Número de série',
      'Handling unit aninhada',
    ],
  },
  {
    titulo: 'Regras comerciais',
    itens: [
      'Entrega total exigida',
      'Política de embalagem',
      'Cliente com restrição',
    ],
  },
  {
    titulo: 'Volume / operação',
    itens: [
      'Multi-zona (picking + sorter)',
      'Lote grande (esgota stock)',
      'Consolidação em colmeia',
    ],
  },
  {
    titulo: 'Casos de erro',
    itens: [
      'Endereço bloqueado',
      'Quantidade negativa',
      'Divergência na recolha',
    ],
  },
]

// 3c: rotulo de item -> flag do contrato backend (so' a 1a familia, 1-de-cada-vez).
const COND_FLAGS: Record<string, string> = {
  'Produto sem stock': 'no_stock',
  'Alocação parcial forçada': 'short_qty',
  'Produto inexistente': 'bad_product',
}
const CONDITION_LABELS: Record<string, string> = {
  no_stock: 'Produto sem stock',
  short_qty: 'Alocação parcial forçada',
  bad_product: 'Produto inexistente',
}

function orderTone(o: OrderReport): 'ok' | 'warn' | 'err' {
  if (o.error || !o.reservations) return o.fully_allocated ? 'ok' : 'err'
  return o.fully_allocated ? 'ok' : 'warn'
}

export default function SaleBatchGenerator() {
  const companyId = useAuthStore((s) => s.user?.company?.id)
  const [nCustomers, setNCustomers] = useState(5)
  const [linesPerOrder, setLinesPerOrder] = useState(3)
  const [qtyPerLine, setQtyPerLine] = useState(2)
  const [cond, setCond] = useState<string | null>(null) // 3c: 1-de-cada-vez (radio-exclusivo)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [report, setReport] = useState<BatchReport | null>(null)

  async function disparar() {
    setError('')
    setLoading(true)
    try {
      const { data } = await api.post('/movatic-dev/triggers/sale-batch/', {
        company: companyId,
        n_customers: nCustomers,
        lines_per_order: linesPerOrder,
        qty_per_line: qtyPerLine,
        conditions: cond ? [cond] : [],
      })
      setReport(data)
    } catch (e: any) {
      const s = e?.response?.status
      const d = e?.response?.data
      setError(
        d?.detail || d?.stock?.[0] || d?.customers?.[0] || `Erro ${s ?? ''}`,
      )
      setReport(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4">
      <div className="mb-1 flex items-center gap-2">
        <FlaskConical className="h-5 w-5 text-sky-500" />
        <h1 className="text-lg font-medium">Criar ordem</h1>
        <span className="rounded border px-1.5 font-mono text-xs opacity-60">
          DV01
        </span>
      </div>
      <p className="mb-5 text-sm opacity-60">
        Injeta um lote de ordens e deixa o motor reagir. Sandbox da tua company.
      </p>

      <div className="mb-1.5 text-xs opacity-70">Tipo de ordem</div>
      <div className="mb-5 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 rounded border-2 border-sky-500/60 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-600">
          <ShoppingCart className="h-4 w-4" />
          Venda
        </span>
        {[
          ['Compra', Package],
          ['Copack', Box],
          ['Inventário', ClipboardList],
        ].map(([t, Icon]: any) => (
          <span
            key={t}
            className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm opacity-50"
          >
            <Icon className="h-4 w-4" />
            {t} <span className="text-xs">· em breve</span>
          </span>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-xs opacity-70">
          Nº de clientes
          <input
            type="number"
            min={1}
            value={nCustomers}
            onChange={(e) => setNCustomers(Math.max(1, +e.target.value || 1))}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-xs opacity-70">
          Linhas por ordem
          <input
            type="number"
            min={1}
            value={linesPerOrder}
            onChange={(e) =>
              setLinesPerOrder(Math.max(1, +e.target.value || 1))
            }
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-xs opacity-70">
          Qty por linha
          <input
            type="number"
            min={1}
            value={qtyPerLine}
            onChange={(e) => setQtyPerLine(Math.max(1, +e.target.value || 1))}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="mb-2.5 flex items-center gap-2">
        <span className="text-xs opacity-70">Condições adicionais</span>
        <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600">
          acendem por fase
        </span>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {FAMILIAS.map((f) => {
          const familiaAtiva = f.itens.some((it) => COND_FLAGS[it])
          return (
            <div
              key={f.titulo}
              className={`rounded-xl border p-3.5 ${familiaAtiva ? '' : 'opacity-60'}`}
            >
              <div className="mb-2 text-sm font-medium">{f.titulo}</div>
              {f.itens.map((it) => {
                const flag = COND_FLAGS[it]
                return (
                  <label
                    key={it}
                    className="mb-1.5 flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      disabled={!flag}
                      checked={!!flag && cond === flag}
                      onChange={() =>
                        flag && setCond(cond === flag ? null : flag)
                      }
                    />
                    {it}
                  </label>
                )
              })}
            </div>
          )
        })}
      </div>

      <button
        onClick={disparar}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded border px-4 py-2 text-sm hover:bg-black/5 disabled:opacity-50"
      >
        <Play className="h-4 w-4" />
        {loading ? 'A disparar…' : 'Disparar lote'}
      </button>

      {error && (
        <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mt-6 border-t pt-5">
        {!report ? (
          <div className="rounded-xl border border-dashed p-8 text-center">
            <p className="text-sm opacity-60">
              Dispara um lote para ver o resultado por ordem.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ['Pool', report.pool_size, ''],
                ['Ordens', report.orders_total, ''],
                ['Alocadas', report.orders_allocated, 'text-emerald-600'],
                ['Parciais', report.orders_partial_or_short, 'text-amber-600'],
              ].map(([l, v, c]: any) => (
                <div key={l} className="rounded-lg bg-black/5 px-4 py-3">
                  <div className="text-xs opacity-60">{l}</div>
                  <div className={`text-xl font-medium ${c}`}>{v}</div>
                </div>
              ))}
            </div>
            {report.condition && (
              <div className="mb-3 text-xs">
                <span className="rounded bg-sky-500/15 px-2 py-0.5 text-sky-600">
                  condição:{' '}
                  {CONDITION_LABELS[report.condition] ?? report.condition}
                </span>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {report.orders.map((o, i) => {
                const tone = orderTone(o)
                const bar =
                  tone === 'ok'
                    ? 'border-l-emerald-500'
                    : tone === 'warn'
                      ? 'border-l-amber-500'
                      : 'border-l-red-500'
                const Icon =
                  tone === 'ok'
                    ? CircleCheck
                    : tone === 'warn'
                      ? AlertTriangle
                      : XCircle
                const ic =
                  tone === 'ok'
                    ? 'text-emerald-500'
                    : tone === 'warn'
                      ? 'text-amber-500'
                      : 'text-red-500'
                return (
                  <div
                    key={o.order_id ?? i}
                    className={`flex items-center gap-3 border border-l-[3px] ${bar} px-3.5 py-2.5`}
                  >
                    <Icon className={`h-4.5 w-4.5 ${ic}`} />
                    <div className="flex-1">
                      <span className="text-sm font-medium">
                        {o.order_id ? `Ordem ${o.order_id}` : 'Ordem'}
                      </span>
                      {o.customer_code && (
                        <span className="text-sm opacity-60">
                          {' '}
                          · {o.customer_code}
                        </span>
                      )}
                      <div className="font-mono text-xs opacity-60">
                        {o.error
                          ? o.error
                          : (o.lines || [])
                              .map((l) => `${l.product_code} ×${l.qty_ordered}`)
                              .join(' · ')}
                        {!o.error &&
                          o.reservations != null &&
                          ` — ${o.reservations} reserva(s)`}
                      </div>
                    </div>
                    {o.status && (
                      <span className="rounded bg-black/5 px-2 py-0.5 text-xs opacity-70">
                        {o.status}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
